import React, { useState, useEffect, useRef } from 'react';
import P from '../pages';
import FormHeader from './FormHeader';
import Alert from './Alert';
import { defaultMessages } from '../../../libs/i18n/default';
import FormControl from 'react-bootstrap/lib/FormControl';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import CheckboxOrRadioGroup from './CheckboxOrRadioGroup';
import { CustomFormGroup, Form } from '../../../shared/components';
import { useSelector } from 'react-redux';
import { pageValid, fieldsToValidate } from '../../../shared/validator';
import { guestProfileSubmit } from '../actions';
import withDefaults from './withDefaults'
import { handleSelection, changeState, getAge } from '../../../shared/utilities';
import { generateProfileElementObject, setProfileElementsFromState } from '../../../shared/profileElementHelpers';

const PetsChildren = ({ content, dispatch, page, territory, environment, user, intl: { formatMessage } }) => {
  const thisPage = P.PETS_CHILDREN_PAGE;
  const [submitted, setSubmitted] = useState(false)
  const dietSelections = useSelector(state => state.localProfileElements.elements.preferences.dietary_requirements_selections)
  const avoidPetTypesSelections = useSelector(state => state.localProfileElements.elements.preferences.avoid_pet_types_selections)
  const preferredVaccinatedHostSelections = useSelector(state => state.localProfileElements.elements.preferences.preferred_vaccinated_host_selections)
  const [fields, setFields] = useState({
    liveWithPets: generateProfileElementObject('', 'preferences', 'live_with_pets'),
    avoidPetTypes: generateProfileElementObject('', 'preferences', 'avoid_pet_types'),
    avoidPetTypesOther: {
      val: setProfileElementsFromState('', 'preferences', 'avoid_pet_types_other'),
      validationKey: 'profile_elements_preferences_avoid_pet_types_other',
      profileElement: 'avoid_pet_types',
      dependentObj: 'avoidPetTypes',
      dependencyMet: (dependentObj = fieldsRef.current.avoidPetTypes) => (dependentObj.val.includes('Other'))
    },
    dietaryRequirements: {
      val: setProfileElementsFromState([], 'preferences', 'dietary_requirements'),
      validationKey: 'profile_elements_preferences_dietary_requirements',
      profileElement: (territory.code == 'SRSC' || territory.code == 'USA') && 'dietary_requirements'
    },
    dietaryRequirementsOther: {
      val: setProfileElementsFromState('', 'preferences', 'dietary_requirements_other'),
      validationKey: 'profile_elements_preferences_dietary_requirements_other',
      profileElement: (territory.code == 'SRSC' || territory.code == 'USA') && 'dietary_requirements',
      dependentObj: 'dietaryRequirements',
      dependencyMet: (dependentObj = fieldsRef.current.dietaryRequirements) => (dependentObj.val.includes('Other'))
    },
    liveWithChildren: generateProfileElementObject('', 'preferences', 'live_with_children'),
    parentNominatedHost: generateProfileElementObject('', 'preferences', 'parent_nominated_host'),
    parentNominatedHostName: generateProfileElementObject('', 'preferences', 'parent_nominated_host_name'),
    preferredVaccinatedHost: generateProfileElementObject('No preference', 'preferences', 'preferred_vaccinated_host'),
    otherRequirements: generateProfileElementObject('', 'preferences', 'other_requirements')
  })
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields

  useEffect(() => {
    fieldsToValidate(fields).forEach(f => {
      handleChange(f, fields[f].val, false)
    })
  }, [])

  const handleChange = (key, val, resetDependentObject = true) => {
    changeState(
      fieldsRef.current,
      key,
      val,
      (newObj) => {
        setFields(prevFields => ({...prevFields, ...newObj}))
      },
      resetDependentObject
    )
  }

  const buildSubmitData = () => {
    const data = {
      live_with_pets: fields.liveWithPets.val,
      avoid_pet_types: fields.avoidPetTypes.val,
      avoid_pet_types_other: fields.avoidPetTypesOther.val,
      live_with_children: fields.liveWithChildren.val,
      parent_nominated_host: fields.parentNominatedHost.val,
      parent_nominated_host_name: fields.parentNominatedHostName.val,
      other_requirements: fields.otherRequirements.val,
      dietary_requirements: fields.dietaryRequirements.val,
      dietary_requirements_other: fields.dietaryRequirementsOther.val,
      preferred_vaccinated_host: fields.preferredVaccinatedHost.val
    }

    if (!territory.disable_covid_questions) {
      data['preferred_vaccinated_host'] = fields.preferredVaccinatedHost.val
    }

    console.log(data);

    return data;
  }

  const submit = (e) => {
    e.preventDefault()
    setSubmitted(true)
    dispatch(guestProfileSubmit({ preferences: buildSubmitData() }, thisPage))
  }

  return (
    <Form page={page} thisPage={thisPage} back={true} skip={environment != 'production'} submitFunc={submit} disableSubmit={!pageValid(fields) || submitted} >
      <FormHeader title={content.pages.pets_children.title} />
      <CustomFormGroup field={fields.liveWithPets}>
        <ControlLabel htmlFor="liveWithPets">{formatMessage(defaultMessages.clientGuestPagesPetsChildrenLabelsPets)}</ControlLabel>
        <FormControl id="liveWithPets"
          type="select"
          componentClass="select"
          defaultValue={fields.liveWithPets.val}
          onChange={(e) => handleChange(e.target.id, e.target.value)}>
          <option value="select" key="select">{formatMessage(defaultMessages.clientCommonYesNo)}</option>
          <option value="true" key="Yes">{formatMessage(defaultMessages.clientCommonAffirmative)}</option>
          <option value="false" key="No">{formatMessage(defaultMessages.clientCommonNegative)}</option>
        </FormControl>
      </CustomFormGroup>

      {(fields.liveWithPets.val === 'false' || fields.liveWithPets.val === false) && (
        <>
          <CustomFormGroup field={fields.avoidPetTypes}>
            <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesPetsChildrenLabelsPetsExclude)}</ControlLabel>
            <CheckboxOrRadioGroup
              setName={'avoidPetTypes'}
              type={'checkbox'}
              checkBoxStyle={'checkbox'}
              controlFunc={e => handleChange('avoidPetTypes', handleSelection(fields.avoidPetTypes.val, e.target.value))}
              options={avoidPetTypesSelections}
              selectedOptions={fields.avoidPetTypes.val}/>
          </CustomFormGroup>

          <CustomFormGroup field={fields.avoidPetTypesOther}>
            <ControlLabel htmlFor="avoidPetTypesOther">{formatMessage(defaultMessages.clientGuestPagesPetsChildrenLabelsPetsExclude)}</ControlLabel>
            <FormControl id="avoidPetTypesOther"
              data-testid="avoidPetTypesOther"
              type="text"
              componentClass="input"
              defaultValue={fields.avoidPetTypesOther.val}
              onChange={(e) => handleChange(e.target.id, e.target.value)}/>
          </CustomFormGroup>
        </>
      )}

      <CustomFormGroup field={fields.liveWithChildren}>
        <ControlLabel htmlFor="liveWithChildren">{formatMessage(defaultMessages.clientGuestPagesPetsChildrenLabelsChildren)}</ControlLabel>
        <FormControl id="liveWithChildren"
          type="select"
          componentClass="select"
          defaultValue={fields.liveWithChildren.val}
          onChange={e => handleChange(e.target.id, e.target.value)} >
          <option value="select" key="select">{formatMessage(defaultMessages.clientCommonYesNo)}</option>
          <option value="true" key="Yes">{formatMessage(defaultMessages.clientCommonAffirmative)}</option>
          <option value="false" key="No">{formatMessage(defaultMessages.clientCommonNegative)}</option>
        </FormControl>
      </CustomFormGroup>

      {getAge(user.dob) <= 18 && (
        <>
          <CustomFormGroup field={fields.parentNominatedHost}>
            <ControlLabel htmlFor="parentNominatedHost">{formatMessage(defaultMessages.clientGuestPagesPetsChildrenLabelsParentNominatedHost)}</ControlLabel>
            <FormControl id="parentNominatedHost"
                         type="select"
                         componentClass="select"
                         defaultValue={fields.parentNominatedHost.val}
                         onChange={e => handleChange(e.target.id, e.target.value)} >
              <option value="select" key="select">{formatMessage(defaultMessages.clientCommonYesNo)}</option>
              <option value="true" key="Yes">{formatMessage(defaultMessages.clientCommonAffirmative)}</option>
              <option value="false" key="No">{formatMessage(defaultMessages.clientCommonNegative)}</option>
            </FormControl>
          </CustomFormGroup>

          {(fields.parentNominatedHost.val === 'true' || fields.parentNominatedHost.val === true) && (
            <CustomFormGroup field={fields.parentNominatedHostName}>
              <ControlLabel htmlFor="parentNominatedHostName">{formatMessage(defaultMessages.clientGuestPagesPetsChildrenLabelsParentNominatedHostName)}</ControlLabel>
              <FormControl id="parentNominatedHostName"
                           type="text"
                           componentClass="input"
                           defaultValue={fields.parentNominatedHostName.val}
                           onChange={(e) => handleChange(e.target.id, e.target.value)}/>
            </CustomFormGroup>
          )}
        </>
      )}

      {!territory.disable_covid_questions && (
        <CustomFormGroup field={fields.preferredVaccinatedHost}>
          <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesPetsChildrenLabelsVaccinatedHost)}</ControlLabel>
          <CheckboxOrRadioGroup
            setName={'preferredVaccinatedHost'}
            type={'radio'}
            checkBoxStyle={'radio-inline'}
            controlFunc={e => handleChange('preferredVaccinatedHost', e.target.value)}
            options={preferredVaccinatedHostSelections}
            selectedOptions={fields.preferredVaccinatedHost.val}/>
        </CustomFormGroup>
      )}

      <CustomFormGroup field={fields.dietaryRequirements}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesProductLabelsDietaryRequirements)}</ControlLabel>
        <CheckboxOrRadioGroup
          setName={'dietaryRequirements'}
          type={'checkbox'}
          checkBoxStyle={'checkbox'}
          controlFunc={e => handleChange('dietaryRequirements', handleSelection(fields.dietaryRequirements.val, e.target.value))}
          options={dietSelections}
          selectedOptions={fields.dietaryRequirements.val}/>
      </CustomFormGroup>

      <CustomFormGroup field={fields.dietaryRequirementsOther}>
        <ControlLabel htmlFor="dietaryRequirementsOther">{formatMessage(defaultMessages.clientGuestPagesProductLabelsDietOther)}</ControlLabel>
        <FormControl id="dietaryRequirementsOther"
          type="text"
          componentClass="input"
          defaultValue={fields.dietaryRequirementsOther.val}
          onChange={(e) => handleChange(e.target.id, e.target.value)}/>
      </CustomFormGroup>

      <CustomFormGroup field={fields.otherRequirements}>
        <ControlLabel htmlFor="otherRequirements">{formatMessage(defaultMessages.clientGuestPagesPetsChildrenLabelsRequirements)}</ControlLabel>
        <FormControl id="otherRequirements"
          type="text"
          componentClass="textarea"
          defaultValue={fields.otherRequirements.val}
          onChange={e => handleChange(e.target.id, e.target.value)}/>
      </CustomFormGroup>

      <Alert alert={content.pages.pets_children.info} />
    </Form>
  )
}

export default withDefaults(PetsChildren);
